"use client";

import { Camera, Edit, FileText, Layers, Lightbulb } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    icon: Lightbulb,
    kanji: "01",
    index: "01",
    title: "Concept Discovery",
    description: "We begin by mapping your brand DNA, identifying the core narrative that drives engagement."
  }, {
    icon: FileText,
    kanji: "02",
    index: "02",
    title: "Script & Treatment",
    description: "Translating strategy into cinematic blueprints, defining the visual and emotional tone."
  }, {
    icon: Camera,
    kanji: "03",
    index: "03",
    title: "Production Execution",
    description: "High-end commercial filming where technical precision meets raw creative energy."
  }, {
    icon: Edit,
    kanji: "04",
    index: "04",
    title: "Post-Production",
    description: "Refining the edit, color grading, and sound design to achieve a polished, final impact."
  }, {
    icon: Layers,
    kanji: "05",
    index: "05",
    title: "Content Scaling",
    description: "Optimizing your assets for multi-platform distribution to maximize reach and ROI."
  }];
  return <section data-section-id="1022" id="our-methodology" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div className="container mx-auto px-5 md:px-12 max-w-[1440px]">
        
        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6 relative transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          
          <div aria-hidden="true" className="pointer-events-none absolute right-0 bottom-0 font-serif italic font-light select-none text-foreground/[0.04] leading-none" style={{
          fontSize: "clamp(8rem, 18vw, 16rem)"
        }}>
            VIVID
          </div>

          
          <div className="md:col-span-3 relative z-10">
            <div className="flex flex-col gap-2">
              
              <span className="font-mono text-[11px] md:text-[13px] uppercase text-muted-foreground tracking-[0.22em] leading-relaxed">The Vivid Process</span>
            </div>
          </div>

          
          <div className="md:col-span-9 relative z-10">
            <h2 className="font-default font-serif font-light leading-[1.02] text-foreground" style={{
            fontSize: "clamp(2.25rem, 5vw, 4.25rem)",
            letterSpacing: "-0.02em"
          }}>
              From initial concept to <em className="italic text-primary">cinematic</em> reality
            </h2>
          </div>
        </div>

        
        <div className="hidden md:block mt-20 relative">
          <div className="h-px w-full bg-border" />
          <div className="absolute inset-0 grid grid-cols-5">
            {steps.map((s, i) => <div key={i} data-index={i} className="flex justify-center -translate-y-1/2">
                <span className="block w-1 h-1 bg-primary rounded-lg" />
              </div>)}
          </div>
        </div>

        
        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-0 relative">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <div key={i} data-index={i} className={`relative px-0 md:px-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                
                {i > 0 && <div className="md:hidden absolute -top-6 left-0 right-0 flex items-center">
                    <div className="h-px flex-1 bg-border" />
                    <span className="block w-1 h-1 bg-primary rounded-lg mx-2" />
                    <div className="h-px flex-1 bg-border" />
                  </div>}

                
                {i < steps.length - 1 && <div aria-hidden="true" className="hidden md:block absolute top-[160px] right-0 w-px h-10 bg-border" />}

                
                <div className="flex md:justify-start">
                  <Icon className="text-primary" strokeWidth={1.25} size={40} />
                </div>

                
                

                
                <h3 className="font-default font-serif font-normal text-foreground mt-4" style={{
              fontSize: "clamp(1.375rem, 2vw, 1.5rem)",
              letterSpacing: "-0.01em",
              lineHeight: 1.15
            }}>
                  {step.title}
                </h3>

                
                <p className="mt-4 font-default text-muted-foreground max-w-[200px]" style={{
              fontSize: "0.9375rem",
              lineHeight: 1.6
            }}>
                  {step.description}
                </p>
              </div>;
        })}
        </div>

        
        <div className="mt-20 md:mt-24 flex justify-center">
          <div className="flex items-center gap-4 max-w-3xl w-full">
            <div className="h-px flex-1 bg-border" />
            <p className="font-default italic text-muted-foreground text-center" style={{
            fontSize: "1.125rem",
            lineHeight: 1.6
          }}>
              Our end-to-end production cycle typically spans 4 to 8 weeks.
            </p>
            <div className="h-px flex-1 bg-border" />
          </div>
        </div>
      </div>
    </section>;
}